import React, { Component } from 'react'
import PropTypes from 'prop-types'
import TransitionGroup from 'react-transition-group/TransitionGroup'
import CSSTransition from 'react-transition-group/CSSTransition'
import { getRemainingTime } from '../utils/stopResume'
import {
  EPISODE_INFO_OBJECT,
  API_PROGRAMME_OBJECT,
} from '../utils/types'

import {
  GenericErrorDialog,
  GeolocationDialog,
  GuidanceDialog,
  LiveStreamErrorDialog,
  LiveStreamRestartErrorDialog,
  LoginDialog,
  NotFoundErrorDialog,
  Overlay,
  PinCheckDialog,
  PostcodeDialog,
  RestartDisabledStreamDialog,
  RestartStreamDialog,
  RestartStreamEndDialog,
  StopResumeDialog,
} from '../components'
import {
  GEOBLOCKED,
  NOT_LOGGED_IN,
  POSTCODE_REQUIRED,
  GUIDANCE_TEXT,
  STOP_RESUME_FOUND,
  LIVE_RESTART_ERROR,
  LIVE_STREAM_ERROR,
  RESTART_DISABLED_STREAM_DIALOG,
  RESTART_STREAM_DIALOG,
  RESTART_STREAM_END,
  PIN_CHECK,
  NOT_FOUND,
  NOT_AVAILABLE,
} from '../constants'

export default function WithDialogsHoc(WrappedComponent) {
  class WithDialogs extends Component {
    getDialogToRender = () => {
      switch (this.props.error) {
        case NOT_FOUND: return NotFoundErrorDialog
        case NOT_AVAILABLE: return NotFoundErrorDialog
        case GEOBLOCKED: return GeolocationDialog
        case NOT_LOGGED_IN: return LoginDialog
        case POSTCODE_REQUIRED: return PostcodeDialog
        case GUIDANCE_TEXT: return GuidanceDialog
        case STOP_RESUME_FOUND: return StopResumeDialog
        case LIVE_STREAM_ERROR: return LiveStreamErrorDialog
        case LIVE_RESTART_ERROR: return LiveStreamRestartErrorDialog
        case RESTART_STREAM_DIALOG: return RestartStreamDialog
        case RESTART_DISABLED_STREAM_DIALOG: return RestartDisabledStreamDialog
        case RESTART_STREAM_END: return RestartStreamEndDialog
        case PIN_CHECK: return PinCheckDialog
        default: return GenericErrorDialog
      }
    }

    getDialogProps = () => {
      const {
        currentUrl,
        currentDomain,
        onPostcodeSubmit,
        videoQueryParams,
        guidanceText,
        episodeInfo: { name: programmeName },
        setGuidanceAccepted,
        stopResumePosition,
        resumeEpisode,
        startEpisode,
        channel,
        error,
        onRestartClick,
        poster,
        onLiveStreamClicked,
        onHomeClicked,
        setPinAccepted,
        title,
        contentType,
        image,
        onErrorStateChange,
      } = this.props

      const { video } = videoQueryParams || {}
      const length = video?.length || 0
      const remainingTime = getRemainingTime({
        duration: length,
        position: stopResumePosition,
      })

      const isLoginDialog = error === NOT_LOGGED_IN
      const isGeolocationDialog = error === GEOBLOCKED
      const isPostcodeDialog = error === POSTCODE_REQUIRED
      const isStopResumeDialog = error === STOP_RESUME_FOUND
      const isLiveRestartErrorDialog = error === LIVE_RESTART_ERROR
      const isLiveStreamErrorDialog = error === LIVE_STREAM_ERROR
      const isRestartDisabledStreamDialog = error === RESTART_DISABLED_STREAM_DIALOG
      const isGuidanceText = error === GUIDANCE_TEXT
      const isRestartStreamDialog = error === RESTART_STREAM_DIALOG
      const isRestartStreamEnd = error === RESTART_STREAM_END
      const isPinCheckDialog = error === PIN_CHECK
      const isNotFoundErrorDialog = (error === NOT_AVAILABLE || error === NOT_FOUND)

      let posterUrl = decodeURIComponent(poster)
      posterUrl = posterUrl.replace('[width]', 1280).replace('[height]', 720)

      const dialogProps = {
        programmeName,
        onErrorStateChange,
        ...(isStopResumeDialog && {
          stopResumePosition,
          remainingTime,
          onResumeEpisode: resumeEpisode,
          onStartEpisode: startEpisode,
        }),
        ...(isGuidanceText && {
          guidanceText,
          onGuidanceAccept: setGuidanceAccepted,
          episodeId: videoQueryParams?.episodeId,
        }),
        ...(isRestartDisabledStreamDialog && {
          poster: posterUrl,
          onLiveStreamClicked,
        }),
        ...(isRestartStreamDialog && {
          poster: posterUrl,
          onLiveStreamClicked,
        }),
        ...(isRestartStreamEnd && {
          poster: posterUrl,
          onLiveStreamClicked,
          onHomeClicked,
        }),
        ...(isLoginDialog && {
          currentUrl,
          currentDomain,
          image,
        }),
        ...(isPostcodeDialog && {
          onPostcodeSubmit,
        }),
        ...(isLiveRestartErrorDialog && {
          channel,
          onRestartClick,
        }),
        ...(isLiveStreamErrorDialog && {
          channel,
        }),
        ...(isGeolocationDialog && {
          contentType,
          currentDomain,
        }),
        ...(isPinCheckDialog && {
          guidanceText,
          setPinAccepted,
        }),
        ...(isNotFoundErrorDialog && {
          title,
        }),
      }
      return dialogProps
    }

    componentDidMount = () => {
      if (this.props.onErrorStateChange) {
        this.props.onErrorStateChange(this.props.error)
      }
    }

    componentDidUpdate = (prevProps) => {
      if (this.props.onErrorStateChange && prevProps.error !== this.props.error) {
        this.props.onErrorStateChange(this.props.error)
      }
    }

    render = () => {
      if (this.props.error) {
        const Dialog = this.getDialogToRender()
        const dialogProps = this.getDialogProps()

        return (
          <Overlay>
            <TransitionGroup
              exit={false}
              component="div"
            >
              <CSSTransition
                classNames="fade"
                timeout={300}
              >
                <Dialog
                  key={this.props.error}
                  {...dialogProps}
                />
              </CSSTransition>
            </TransitionGroup>
          </Overlay>
        )
      }

      return <WrappedComponent {...this.props} />
    }
  }

  WithDialogs.propTypes = {
    stopResumePosition: PropTypes.number,
    videoQueryParams: PropTypes.object,
    episodeInfo: EPISODE_INFO_OBJECT,
    resumeEpisode: PropTypes.func,
    startEpisode: PropTypes.func,
    currentUrl: PropTypes.string,
    guidanceText: PropTypes.string,
    currentDomain: PropTypes.string,
    userAgent: PropTypes.string,
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
      PropTypes.shape({
        type: PropTypes.string,
        message: PropTypes.string,
      }),
    ]),
    onPostcodeSubmit: PropTypes.func,
    pinAcceptance: PropTypes.shape({
      encodedPin: PropTypes.string,
    }),
    currentEpisode: PropTypes.shape({
      encodedPin: PropTypes.string,
    }),
    setGuidanceAccepted: PropTypes.func,
    channel: PropTypes.string,
    onRestartClick: PropTypes.func,
    poster: PropTypes.string,
    onLiveStreamClicked: PropTypes.func,
    onFromStartStreamClicked: PropTypes.func,
    onHomeClicked: PropTypes.func,
    setPinAccepted: PropTypes.func,
    encodedPin: PropTypes.string,
    image: PropTypes.string,
    title: PropTypes.string,
    programme: API_PROGRAMME_OBJECT,
    groupToken: PropTypes.string,
    contentType: PropTypes.oneOf(['live', 'vod']),
    onErrorStateChange: PropTypes.func,
  }

  WithDialogs.defaultProps = {
    stopResumePosition: 0,
    videoQueryParams: {},
    episodeInfo: {},
    resumeEpisode: () => '',
    startEpisode: () => '',
    currentUrl: null,
    guidanceText: null,
    currentDomain: null,
    userAgent: null,
    error: null,
    poster: null,
    onRestartClick: () => {},
    onLiveStreamClicked: () => {},
    onFromStartStreamClicked: () => {},
    onHomeClicked: () => {},
    onPostcodeSubmit: () => {},
    setGuidanceAccepted: () => {},
    setPinAccepted: () => {},
    encodedPin: null,
    programme: {},
    channel: 'stv',
    title: null,
    image: null,
    groupToken: null,
    contentType: null,
    onErrorStateChange: null,
  }

  return WithDialogs
}
